import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, Loader2, Minus, RefreshCw, Search, TrendingDown, TrendingUp, UserRound } from 'lucide-react';
import {
  cancelAdminAccountSubscription,
  getAdminAccountPaymentDebug,
  getAdminAccountPaymentSummaries,
  requestAdminAccountRefund,
} from '@edusense/payment-client/client_gen_gen_generated/admin';
import { useAuth } from '@edusense/shared-auth-core';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  ADMIN_ACCOUNT_PAGE_SIZE,
  ADMIN_ACCOUNT_DEFAULT_SORT_MODE,
  ADMIN_ACCOUNT_SORT_OPTIONS,
  ADMIN_ACCOUNT_SUBSCRIPTION_FILTER_OPTIONS,
  countAdminAccountCreatedAfter,
  countCurrentAdminAccountPaidSubscribers,
  filterAdminAccountsBySubscription,
  type AdminAccountPaymentSummaryResponse,
  type AdminAccountPaymentDebugResponse,
  type AdminAccountPaymentInsightTrendSummary,
  type AdminAccountReferralDebugResponse,
  type AdminAccountSignupTrend,
  type AdminAccountSortMode,
  type AdminAccountSubscriptionFilter,
  type AdminAccountUser,
  formatAdminAccountDateTime,
  getAdminAccountUserKey,
  getAdminAccountUserLabel,
  getAdminAccountUserSearchText,
  paginateAdminAccountItems,
  resolveLatestAdminAccountCreatedDate,
  normalizeAdminAccountPaymentDebug,
  normalizeAdminAccountPaymentSummaries,
  normalizeAdminAccountReferralDebug,
  normalizeAdminAccountUserId,
  normalizeAdminAccountUsers,
  formatAdminAccountStatus,
  resolveAdminAccountSubscriptionDateValue,
  sortAdminAccountUsers,
  summarizeAdminAccountMonthlyPaymentInsights,
  summarizeAdminAccountSignupTrends,
} from '../types/account';
import { getAdminAuthServerUrl } from '../auth/adminOAuth';
import { getAdminPaymentRequestOptions } from '../payment/adminPaymentRequestConfig';
import {
  AccountActionState,
  ExpandedAccountDetails,
  ManualBenefitGrantOptions,
  formatCompactText,
} from './accounts/ExpandedAccountDetails';
import { AccountSummaryCell } from './accounts/AccountSummaryCell';
import type { SubscriptionCancelChoice } from '../components/SubscriptionCancelPanel';
import { useAdminApplicationScope } from '../apps/AdminApplicationScope';
import {
  matchesAdminPaymentAppKey,
  matchesAdminPlanCode,
  matchesAdminRegistrationApplication,
} from '../apps/adminApplications';

type FetchState = {
  loading: boolean;
  error: string | null;
};

type AdminReferralResetResponse = {
  success?: boolean;
  message?: string;
};

type AdminFirstLoginTrialResetResponse = {
  success?: boolean;
  message?: string;
};

type AdminManualBenefitGrantResponse = {
  success?: boolean;
  message?: string;
};

type ReferralResetScope = 'referee' | 'referrer';
type PaymentSummaryLoadScope = 'page' | 'all';

const ADMIN_ACCOUNTS_LAST_CHECKED_CREATED_DATE_KEY = 'admin.accounts.lastCheckedCreatedDate';
const ADMIN_ACCOUNT_PAYMENT_SUMMARY_BATCH_SIZE = 50;
const ADMIN_ACCOUNT_PAYMENT_SUMMARY_CONCURRENCY = 3;

const chunkAdminAccountUserIds = (userIds: string[]): string[][] => {
  const chunks: string[][] = [];
  for (let index = 0; index < userIds.length; index += ADMIN_ACCOUNT_PAYMENT_SUMMARY_BATCH_SIZE) {
    chunks.push(userIds.slice(index, index + ADMIN_ACCOUNT_PAYMENT_SUMMARY_BATCH_SIZE));
  }
  return chunks;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const message = record['message'] || record['error'] || record['detail'];
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return '알 수 없는 오류가 발생했습니다.';
};

export default function AccountsPage() {
  const { application, applicationId } = useAdminApplicationScope();
  const isSajugApplication = applicationId === 'sajug';
  const lastCheckedStorageKey = `${ADMIN_ACCOUNTS_LAST_CHECKED_CREATED_DATE_KEY}.${applicationId}`;
  const { token: accessToken, isAuthenticated, isLoading: authLoading } = useAuth();
  const adminPaymentRequestOptions = getAdminPaymentRequestOptions();
  const [accounts, setAccounts] = useState<AdminAccountUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState<AdminAccountSortMode>(ADMIN_ACCOUNT_DEFAULT_SORT_MODE);
  const [subscriptionFilter, setSubscriptionFilter] = useState<AdminAccountSubscriptionFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastCheckedCreatedDate, setLastCheckedCreatedDate] = useState<string | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    return window.localStorage.getItem(lastCheckedStorageKey);
  });
  const [expandedAccountId, setExpandedAccountId] = useState('');
  const [paymentDebug, setPaymentDebug] = useState<AdminAccountPaymentDebugResponse | null>(null);
  const [referralDebug, setReferralDebug] = useState<AdminAccountReferralDebugResponse | null>(null);
  const [paymentSummariesByUserId, setPaymentSummariesByUserId] = useState<Record<string, AdminAccountPaymentSummaryResponse>>({});
  const [listState, setListState] = useState<FetchState>({ loading: false, error: null });
  const [debugState, setDebugState] = useState<FetchState>({ loading: false, error: null });
  const [referralState, setReferralState] = useState<FetchState>({ loading: false, error: null });
  const [summaryState, setSummaryState] = useState<FetchState>({ loading: false, error: null });
  const [allPaymentSummariesLoaded, setAllPaymentSummariesLoaded] = useState(false);
  const [actionState, setActionState] = useState<AccountActionState>({ loadingKey: null, error: null, message: null });

  const allAccountUserIds = useMemo(
    () => accounts
      .map((account) => normalizeAdminAccountUserId(account))
      .filter((userId) => userId.length > 0),
    [accounts],
  );

  const scopedPaymentSummariesByUserId = useMemo(
    () => Object.fromEntries(
      Object.entries(paymentSummariesByUserId).flatMap(([userId, summary]) => {
        const appGroups = summary.appGroups.filter((group) =>
          matchesAdminPaymentAppKey(applicationId, group.appKey)
        );
        return appGroups.length > 0 ? [[userId, { ...summary, appGroups }]] : [];
      }),
    ) as Record<string, AdminAccountPaymentSummaryResponse>,
    [applicationId, paymentSummariesByUserId],
  );

  const applicationAccounts = useMemo(() => {
    if (!allPaymentSummariesLoaded) {
      return [];
    }
    return accounts.filter((account) => {
      const userId = normalizeAdminAccountUserId(account);
      const matchesRegistrationApplication = matchesAdminRegistrationApplication(
        applicationId,
        account.registrationApplications,
      );
      const matchesPaymentApplication = Boolean(userId && scopedPaymentSummariesByUserId[userId]);
      return matchesRegistrationApplication || matchesPaymentApplication;
    });
  }, [
    accounts,
    allPaymentSummariesLoaded,
    applicationId,
    scopedPaymentSummariesByUserId,
  ]);

  const filteredAccounts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const searchFilteredAccounts = term
      ? applicationAccounts.filter((account) => getAdminAccountUserSearchText(account).includes(term))
      : applicationAccounts;

    if (subscriptionFilter === 'all') {
      return searchFilteredAccounts;
    }
    if (!allPaymentSummariesLoaded) {
      return [];
    }

    return filterAdminAccountsBySubscription(
      searchFilteredAccounts,
      scopedPaymentSummariesByUserId,
      subscriptionFilter,
    );
  }, [allPaymentSummariesLoaded, applicationAccounts, scopedPaymentSummariesByUserId, searchTerm, subscriptionFilter]);

  const sortedAccounts = useMemo(
    () => sortAdminAccountUsers(filteredAccounts, sortMode),
    [filteredAccounts, sortMode],
  );

  const signupTrends = useMemo(() => summarizeAdminAccountSignupTrends(applicationAccounts), [applicationAccounts]);
  const paymentInsights = useMemo(
    () => summarizeAdminAccountMonthlyPaymentInsights(scopedPaymentSummariesByUserId),
    [scopedPaymentSummariesByUserId],
  );
  const currentPaidSubscriberCount = useMemo(
    () => countCurrentAdminAccountPaidSubscribers(scopedPaymentSummariesByUserId),
    [scopedPaymentSummariesByUserId],
  );
  const uncheckedSignupCount = useMemo(
    () => countAdminAccountCreatedAfter(applicationAccounts, lastCheckedCreatedDate),
    [applicationAccounts, lastCheckedCreatedDate],
  );

  const scopedPaymentDebug = useMemo<AdminAccountPaymentDebugResponse | null>(() => {
    if (!paymentDebug) return null;
    const activeSubscription = matchesAdminPlanCode(
      applicationId,
      paymentDebug.activeSubscription?.productPlanCode,
    ) ? paymentDebug.activeSubscription : null;
    return {
      ...paymentDebug,
      currentPlanCode: matchesAdminPlanCode(applicationId, paymentDebug.currentPlanCode)
        ? paymentDebug.currentPlanCode
        : '',
      activeSubscription,
      recentOrders: paymentDebug.recentOrders.filter((order) =>
        matchesAdminPlanCode(applicationId, order.productPlanCode)
      ),
      recentSubscriptionEvents: isSajugApplication ? paymentDebug.recentSubscriptionEvents : [],
    };
  }, [applicationId, isSajugApplication, paymentDebug]);

  const paginatedAccounts = useMemo(
    () => paginateAdminAccountItems(sortedAccounts, currentPage, ADMIN_ACCOUNT_PAGE_SIZE),
    [currentPage, sortedAccounts],
  );

  const paginatedUserIds = useMemo(
    () =>
      paginatedAccounts.items
        .map((account) => normalizeAdminAccountUserId(account))
        .filter((userId) => userId.length > 0),
    [paginatedAccounts.items],
  );

  const loadAccounts = useCallback(async (signal?: AbortSignal) => {
    setListState({ loading: true, error: null });
    try {
      const response = await axios.get<unknown>('/api/admin/users', {
        baseURL: getAdminAuthServerUrl(),
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        withCredentials: true,
        signal,
      });
      const payload = response.data;
      const nextAccounts = normalizeAdminAccountUsers(payload);
      setAccounts(nextAccounts);
      setPaymentSummariesByUserId({});
      setAllPaymentSummariesLoaded(false);
      setExpandedAccountId((current) =>
        nextAccounts.some((account) => normalizeAdminAccountUserId(account) === current) ? current : ''
      );
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      setAccounts([]);
      setExpandedAccountId('');
      setPaymentDebug(null);
      setPaymentSummariesByUserId({});
      setAllPaymentSummariesLoaded(false);
      setListState({ loading: false, error: getErrorMessage(error) });
      return;
    }

    setListState({ loading: false, error: null });
  }, [accessToken]);

  const loadPaymentDebug = useCallback(async (userId: string, signal?: AbortSignal) => {
    if (!userId) return;

    setDebugState({ loading: true, error: null });
    setPaymentDebug(null);

    try {
      const payload = await getAdminAccountPaymentDebug(
        {
          userId,
        },
        adminPaymentRequestOptions,
        signal,
      );
      setPaymentDebug(normalizeAdminAccountPaymentDebug(payload));
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      setPaymentDebug(null);
      setDebugState({ loading: false, error: getErrorMessage(error) });
      return;
    }

    setDebugState({ loading: false, error: null });
  }, []);

  const loadPaymentSummaries = useCallback(async (
    userIds: string[],
    signal?: AbortSignal,
    scope: PaymentSummaryLoadScope = 'page',
  ) => {
    const normalizedUserIds = [...new Set(userIds.map((userId) => userId.trim()).filter(Boolean))];
    if (normalizedUserIds.length === 0) {
      setPaymentSummariesByUserId({});
      if (scope === 'all') {
        setAllPaymentSummariesLoaded(true);
      }
      setSummaryState({ loading: false, error: null });
      return;
    }

    setSummaryState({ loading: true, error: null });
    try {
      const userIdChunks = chunkAdminAccountUserIds(normalizedUserIds);
      const summaries: AdminAccountPaymentSummaryResponse[] = [];

      for (let index = 0; index < userIdChunks.length; index += ADMIN_ACCOUNT_PAYMENT_SUMMARY_CONCURRENCY) {
        const payloads = await Promise.all(
          userIdChunks
            .slice(index, index + ADMIN_ACCOUNT_PAYMENT_SUMMARY_CONCURRENCY)
            .map((batchUserIds) => getAdminAccountPaymentSummaries(
              { userIds: batchUserIds },
              adminPaymentRequestOptions,
              signal,
            )),
        );
        payloads.forEach((payload) => {
          summaries.push(...normalizeAdminAccountPaymentSummaries(payload));
        });
      }

      setPaymentSummariesByUserId(
        Object.fromEntries(summaries.map((summary) => [summary.userId, summary]))
      );
      if (scope === 'all') {
        setAllPaymentSummariesLoaded(true);
      }
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      setPaymentSummariesByUserId({});
      if (scope === 'all') {
        setAllPaymentSummariesLoaded(false);
      }
      setSummaryState({ loading: false, error: getErrorMessage(error) });
      return;
    }

    setSummaryState({ loading: false, error: null });
  }, []);

  const loadReferralDebug = useCallback(async (userId: string, signal?: AbortSignal) => {
    if (!userId) return;
    if (!accessToken) {
      setReferralDebug(null);
      setReferralState({ loading: false, error: '관리자 인증 토큰이 없습니다. 다시 로그인해주세요.' });
      return;
    }

    setReferralState({ loading: true, error: null });
    setReferralDebug(null);

    try {
      const response = await axios.get('/api/referral/admin/debug', {
        params: { user_id: userId },
        signal,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setReferralDebug(normalizeAdminAccountReferralDebug(response.data));
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      setReferralDebug(null);
      setReferralState({ loading: false, error: getErrorMessage(error) });
      return;
    }

    setReferralState({ loading: false, error: null });
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const controller = new AbortController();
    void loadAccounts(controller.signal);
    return () => controller.abort();
  }, [isAuthenticated, loadAccounts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortMode, subscriptionFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setExpandedAccountId('');
    setPaymentDebug(null);
    setReferralDebug(null);
    setLastCheckedCreatedDate(window.localStorage.getItem(lastCheckedStorageKey));
    setActionState({ loadingKey: null, error: null, message: null });
  }, [applicationId, lastCheckedStorageKey]);

  useEffect(() => {
    if (currentPage !== paginatedAccounts.currentPage) {
      setCurrentPage(paginatedAccounts.currentPage);
    }
  }, [currentPage, paginatedAccounts.currentPage]);

  useEffect(() => {
    if (!isAuthenticated || !expandedAccountId) {
      setPaymentDebug(null);
      setDebugState({ loading: false, error: null });
      return;
    }

    const controller = new AbortController();
    void loadPaymentDebug(expandedAccountId, controller.signal);
    return () => controller.abort();
  }, [expandedAccountId, isAuthenticated, loadPaymentDebug]);

  useEffect(() => {
    if (!isAuthenticated || !isSajugApplication || !expandedAccountId) {
      setReferralDebug(null);
      setReferralState({ loading: false, error: null });
      return;
    }

    const controller = new AbortController();
    void loadReferralDebug(expandedAccountId, controller.signal);
    return () => controller.abort();
  }, [expandedAccountId, isAuthenticated, isSajugApplication, loadReferralDebug]);

  useEffect(() => {
    setActionState({ loadingKey: null, error: null, message: null });
  }, [expandedAccountId]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPaymentSummariesByUserId({});
      setAllPaymentSummariesLoaded(false);
      setSummaryState({ loading: false, error: null });
      return;
    }
    if (allPaymentSummariesLoaded) {
      return;
    }

    const controller = new AbortController();
    void loadPaymentSummaries(
      allAccountUserIds,
      controller.signal,
      'all',
    );
    return () => controller.abort();
  }, [
    allAccountUserIds,
    allPaymentSummariesLoaded,
    isAuthenticated,
    loadPaymentSummaries,
  ]);

  const totalCount = applicationAccounts.length;
  const identityAccountCount = accounts.length;
  const visibleCount = sortedAccounts.length;
  const selectedSortLabel = ADMIN_ACCOUNT_SORT_OPTIONS.find((option) => option.value === sortMode)?.label ?? '최근 가입순';
  const selectedSubscriptionFilterLabel = ADMIN_ACCOUNT_SUBSCRIPTION_FILTER_OPTIONS.find(
    (option) => option.value === subscriptionFilter,
  )?.label ?? '전체 계정';
  const subscriptionFilterLoading = !allPaymentSummariesLoaded && summaryState.loading;
  const subscriptionFilterUnavailable = !allPaymentSummariesLoaded && Boolean(summaryState.error);
  const paymentSummaryRefreshUserIds = allPaymentSummariesLoaded ? allAccountUserIds : paginatedUserIds;
  const paymentSummaryRefreshScope: PaymentSummaryLoadScope = allPaymentSummariesLoaded ? 'all' : 'page';
  const selectClasses =
    'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

  const toggleAccountRow = (accountId: string) => {
    setExpandedAccountId((current) => (current === accountId ? '' : accountId));
  };

  const handleMarkSignupsChecked = () => {
    const latestCreatedDate = resolveLatestAdminAccountCreatedDate(accounts);
    if (!latestCreatedDate) {
      return;
    }
    window.localStorage.setItem(lastCheckedStorageKey, latestCreatedDate);
    setLastCheckedCreatedDate(latestCreatedDate);
  };

  const handleRequestRefund = useCallback(async (userId: string, orderId: string) => {
    const reason = window.prompt('환불 사유를 입력하세요.', '관리자 확인 후 환불');
    if (reason === null) {
      return;
    }

    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setActionState({ loadingKey: null, error: '환불 사유를 입력해야 합니다.', message: null });
      return;
    }

    const loadingKey = `refund:${orderId}`;
    setActionState({ loadingKey, error: null, message: null });

    try {
      const response = await requestAdminAccountRefund({
        userId,
        orderId,
        reason: trimmedReason,
      }, adminPaymentRequestOptions);

      await Promise.all([
        expandedAccountId === userId ? loadPaymentDebug(userId) : Promise.resolve(),
        loadPaymentSummaries(paymentSummaryRefreshUserIds, undefined, paymentSummaryRefreshScope),
      ]);

      setActionState({
        loadingKey: null,
        error: null,
        message: response.message || '환불 요청이 접수되었습니다.',
      });
    } catch (error) {
      setActionState({ loadingKey: null, error: getErrorMessage(error), message: null });
    }
  }, [
    adminPaymentRequestOptions,
    expandedAccountId,
    loadPaymentDebug,
    loadPaymentSummaries,
    paymentSummaryRefreshScope,
    paymentSummaryRefreshUserIds,
  ]);

  const handleCancelSubscription = useCallback(async (
    userId: string,
    subscriptionId: number | string,
    choice: SubscriptionCancelChoice,
  ) => {
    const numericSubscriptionId = Number(subscriptionId);
    if (!Number.isFinite(numericSubscriptionId)) {
      setActionState({ loadingKey: null, error: '유효한 구독 ID를 찾지 못했습니다.', message: null });
      return;
    }

    const loadingKey = `cancel:${numericSubscriptionId}`;
    setActionState({ loadingKey, error: null, message: null });

    try {
      // immediate 는 Paddle 구독만 쓴다(생략·false = 결제 기간 끝에 해지).
      const request = { userId, subscriptionId: numericSubscriptionId, reason: choice.reason, immediate: choice.immediate };
      const response = await cancelAdminAccountSubscription(request, adminPaymentRequestOptions);

      await Promise.all([
        expandedAccountId === userId ? loadPaymentDebug(userId) : Promise.resolve(),
        loadPaymentSummaries(paymentSummaryRefreshUserIds, undefined, paymentSummaryRefreshScope),
      ]);

      setActionState({
        loadingKey: null,
        error: null,
        message: response.message || (choice.immediate ? '구독을 즉시 해지했습니다.' : '구독 해지를 요청했습니다.'),
      });
    } catch (error) {
      setActionState({ loadingKey: null, error: getErrorMessage(error), message: null });
    }
  }, [
    adminPaymentRequestOptions,
    expandedAccountId,
    loadPaymentDebug,
    loadPaymentSummaries,
    paymentSummaryRefreshScope,
    paymentSummaryRefreshUserIds,
  ]);

  const handleResetReferral = useCallback(async (
    userId: string,
    email: string,
    resetScope: ReferralResetScope,
  ) => {
    if (!accessToken) {
      setActionState({ loadingKey: null, error: '관리자 인증 토큰이 없습니다. 다시 로그인해주세요.', message: null });
      return;
    }

    const scopeLabel = resetScope === 'referrer' ? '추천인 사용당한 이력' : '추천인 사용 이력';
    const confirmed = window.confirm(
      [
        `${email || userId} 계정의 ${scopeLabel}을 리셋합니다.`,
        '이미 적립된 포인트는 차감됩니다.',
        '이미 지급된 Pro 기간은 자동 롤백되지 않을 수 있습니다.',
        '',
        '계속 진행하시겠습니까?',
      ].join('\n'),
    );
    if (!confirmed) {
      return;
    }

    const loadingKey =
      resetScope === 'referrer' ? `referral-reset-received:${userId}` : `referral-reset-used:${userId}`;
    setActionState({ loadingKey, error: null, message: null });

    try {
      const response = await axios.post<AdminReferralResetResponse>(
        '/api/referral/admin/reset',
        { user_id: userId, reset_scope: resetScope },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (expandedAccountId === userId) {
        await loadReferralDebug(userId);
      }

      setActionState({
        loadingKey: null,
        error: null,
        message: response.data?.message || `${scopeLabel}이 리셋되었습니다.`,
      });
    } catch (error) {
      setActionState({ loadingKey: null, error: getErrorMessage(error), message: null });
    }
  }, [accessToken, expandedAccountId, loadReferralDebug]);

  const handleResetReferralUsedHistory = useCallback(
    async (userId: string, email: string) => handleResetReferral(userId, email, 'referee'),
    [handleResetReferral],
  );

  const handleResetReferralReceivedHistory = useCallback(
    async (userId: string, email: string) => handleResetReferral(userId, email, 'referrer'),
    [handleResetReferral],
  );

  const handleReloadReferralDebug = useCallback(
    async (userId: string) => {
      await loadReferralDebug(userId);
    },
    [loadReferralDebug],
  );

  const handleResetFirstLoginTrial = useCallback(async (userId: string, email: string) => {
    if (!accessToken) {
      setActionState({ loadingKey: null, error: '관리자 인증 토큰이 없습니다. 다시 로그인해주세요.', message: null });
      return;
    }

    const confirmed = window.confirm(
      [
        `${email || userId} 계정의 첫 로그인 혜택 지급 이력을 리셋합니다.`,
        '기존 Pro 구독 기간이나 결제 서버 구독은 자동 롤백되지 않습니다.',
        '활성 구독이 남아 있으면 다음 로그인에서도 첫 로그인 혜택은 다시 지급되지 않을 수 있습니다.',
        '',
        '계속 진행하시겠습니까?',
      ].join('\n'),
    );
    if (!confirmed) {
      return;
    }

    setActionState({ loadingKey: `first-login-trial-reset:${userId}`, error: null, message: null });

    try {
      const response = await axios.post<AdminFirstLoginTrialResetResponse>(
        '/api/benefit-campaign/admin/first-login-trial/reset',
        { user_id: userId },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      setActionState({
        loadingKey: null,
        error: null,
        message: response.data?.message || '첫 로그인 혜택 지급 이력을 리셋했습니다.',
      });
    } catch (error) {
      setActionState({ loadingKey: null, error: getErrorMessage(error), message: null });
    }
  }, [accessToken]);

  const handleGrantManualBenefit = useCallback(async (
    userId: string,
    email: string,
    options: ManualBenefitGrantOptions,
  ) => {
    if (!accessToken) {
      setActionState({ loadingKey: null, error: '관리자 인증 토큰이 없습니다. 다시 로그인해주세요.', message: null });
      return;
    }

    const durationDays = Math.max(1, Math.min(365, Math.floor(Number(options.durationDays) || 30)));
    const reason = options.reason.trim() || '관리자 수동 혜택';
    const confirmed = window.confirm(
      [
        `${email || userId} 계정에 ${options.planCode} ${durationDays}일 혜택을 지급합니다.`,
        `사유: ${reason}`,
        '기존 활성 구독이 있으면 결제 서버에서 만료일을 연장합니다.',
        '',
        '계속 진행하시겠습니까?',
      ].join('\n'),
    );
    if (!confirmed) {
      return;
    }

    setActionState({ loadingKey: `manual-benefit-grant:${userId}`, error: null, message: null });

    try {
      const response = await axios.post<AdminManualBenefitGrantResponse>(
        '/api/benefit-campaign/admin/manual-grant',
        {
          user_id: userId,
          durationDays,
          planCode: options.planCode,
          reason,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      await Promise.all([
        expandedAccountId === userId ? loadPaymentDebug(userId) : Promise.resolve(),
        loadPaymentSummaries(paymentSummaryRefreshUserIds, undefined, paymentSummaryRefreshScope),
      ]);

      setActionState({
        loadingKey: null,
        error: null,
        message: response.data?.message || `${durationDays}일 혜택을 지급했습니다.`,
      });
    } catch (error) {
      setActionState({ loadingKey: null, error: getErrorMessage(error), message: null });
    }
  }, [
    accessToken,
    expandedAccountId,
    loadPaymentDebug,
    loadPaymentSummaries,
    paymentSummaryRefreshScope,
    paymentSummaryRefreshUserIds,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold tracking-wide text-muted-foreground">{application.label} · 서비스 운영</p>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">계정 및 구독</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          계정별 이용 권한과 결제 일정을 확인합니다. {application.label} 결제·쿠폰·혜택 근거가 있는 계정을 표시합니다.
        </p>
      </div>

      {!application.capabilities.payPurchaseVerification ? (
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="py-4 text-sm text-amber-950">
            {application.label} Play 구매 토큰은 아직 Pay 서버 검증 API로 전송되지 않습니다. 따라서 이 화면에는
            Pay에 이미 남은 결제·혜택 근거만 표시되며, 앱 내부에서만 확인된 구매는 계정이나 결제 지표에 잡히지 않습니다.
          </CardContent>
        </Card>
      ) : null}

      {!authLoading && !isAuthenticated && (
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">
            로그인 상태가 아니어서 계정 디버그 데이터를 불러올 수 없습니다.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <SignupTrendCard
          title={`이번 주 ${application.label} 식별 계정`}
          previousLabel="직전 주"
          trend={signupTrends.week}
        />
        <SignupTrendCard
          title={`이번 달 ${application.label} 식별 계정`}
          previousLabel="직전 달"
          trend={signupTrends.month}
        />
          <CurrentPaymentInsightCard
            title="현재 확인된 유료 구독자"
            description="Play·PG 실결제 후 현재 이용 가능한 계정"
            count={currentPaidSubscriberCount}
            loading={summaryState.loading && !allPaymentSummariesLoaded}
          />
      </div>

      <details className="group rounded-xl border bg-card">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-xl px-5 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
          <div>
            <h2 className="text-sm font-semibold">이번 달 구독·혜택 인사이트</h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              신규 실결제, 무료체험, 쿠폰과 운영 혜택을 구분해 확인합니다.
            </p>
          </div>
          <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
        </summary>
        <div className="grid gap-3 border-t p-4 sm:grid-cols-2 xl:grid-cols-3">

          <PaymentInsightCard
            title="이번 달 신규 실결제"
            description="Play·PG 결제 수신이 확인된 신규 구독"
            trend={paymentInsights.confirmedPaidSubscribers}
            loading={summaryState.loading && !allPaymentSummariesLoaded}
          />
          <PaymentInsightCard
            title="Google 구매 검증"
            description="Play에서 구매 토큰이 확인된 신규 건"
            trend={paymentInsights.googlePlayPurchases}
            loading={summaryState.loading && !allPaymentSummariesLoaded}
          />
          <PaymentInsightCard
            title="Offer 무료체험"
            description="스토어 응답으로 확인된 무료체험"
            trend={paymentInsights.offerTrials}
            loading={summaryState.loading && !allPaymentSummariesLoaded}
          />
          {isSajugApplication ? (
            <PaymentInsightCard
              title="첫 로그인 혜택"
              description="첫 로그인 쿠폰 또는 캠페인 지급 사용자"
              trend={paymentInsights.firstLoginBenefits}
              loading={summaryState.loading && !allPaymentSummariesLoaded}
            />
          ) : null}
          <PaymentInsightCard
            title="쿠폰 사용자"
            description="첫 로그인·추천인 제외 운영 쿠폰"
            trend={paymentInsights.couponUsers}
            loading={summaryState.loading && !allPaymentSummariesLoaded}
          />
          {isSajugApplication ? (
            <PaymentInsightCard
              title="추천인 보상"
              description="추천인 직접 보상 또는 추천인 쿠폰 · 이번 달 지급"
              trend={paymentInsights.referralRewards}
              loading={summaryState.loading && !allPaymentSummariesLoaded}
            />
          ) : null}
          <PaymentInsightCard
            title="운영 혜택"
            description="관리자 수동 혜택 지급 사용자"
            trend={paymentInsights.manualBenefits}
            loading={summaryState.loading && !allPaymentSummariesLoaded}
          />
        </div>
        <p className="px-5 pb-4 text-xs text-muted-foreground">혜택별 계정 중복은 한 명으로 집계합니다. 현재 유료 이용자와 이번 달 신규 실결제는 서로 다른 지표입니다.</p>
      </details>

      <Card>
        <CardHeader className="space-y-5">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-lg">계정 목록</CardTitle>
                {uncheckedSignupCount > 0 ? <Badge variant="warning">+{uncheckedSignupCount} 신규</Badge> : null}
              </div>
              <p className="text-sm text-muted-foreground">
                {subscriptionFilterLoading
                  ? `인증 계정 ${identityAccountCount}명 중 ${application.label} 귀속 대상을 계산 중...`
                  : `인증 계정 ${identityAccountCount}명 · 선택 범위 ${totalCount}명 · ${paginatedAccounts.startIndex}-${paginatedAccounts.endIndex} / ${visibleCount} · ${selectedSubscriptionFilterLabel} · ${selectedSortLabel}`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {uncheckedSignupCount > 0 ? (
                <Button type="button" variant="secondary" size="sm" onClick={handleMarkSignupsChecked}>
                  신규 확인 처리
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void loadAccounts()}
                disabled={listState.loading}
              >
                <RefreshCw className={`mr-1 h-4 w-4 ${listState.loading ? 'animate-spin' : ''}`} />
                새로고침
              </Button>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px_220px]">
            <div className="space-y-1.5">
              <label htmlFor="account-search" className="text-xs font-medium text-muted-foreground">계정 검색</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="account-search"
                  className="pl-9"
                  placeholder="이름 또는 이메일로 검색"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="account-filter" className="text-xs font-medium text-muted-foreground">계정 구분</label>
              <select
                className={selectClasses}
                value={subscriptionFilter}
                onChange={(event) => setSubscriptionFilter(event.target.value as AdminAccountSubscriptionFilter)}
                id="account-filter"
                aria-label="계정 구분 필터"
              >
                {ADMIN_ACCOUNT_SUBSCRIPTION_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="account-sort" className="text-xs font-medium text-muted-foreground">정렬 기준</label>
              <select
                className={selectClasses}
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value as AdminAccountSortMode)}
                id="account-sort"
                aria-label="계정 정렬"
              >
                {ADMIN_ACCOUNT_SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 pb-5 sm:px-5 lg:px-6">
          {listState.error && (
            <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold">사용자 목록을 불러오지 못했습니다.</p>
                  <p className="mt-1 text-destructive/80">{listState.error}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => void loadAccounts()}>
                  다시 시도
                </Button>
              </div>
            </div>
          )}

          {summaryState.error && !listState.error && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold">
                    {subscriptionFilterUnavailable
                      ? '계정 구분에 필요한 전체 이용·결제 요약을 불러오지 못했습니다.'
                      : '이용·결제 요약을 불러오지 못했습니다.'}
                  </p>
                  <p className="mt-1 text-amber-800/80">{summaryState.error}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void loadPaymentSummaries(
                    allAccountUserIds,
                    undefined,
                    'all',
                  )}
                  disabled={summaryState.loading}
                >
                  다시 시도
                </Button>
              </div>
            </div>
          )}

          <div className="rounded-lg md:border">
            <Table mobileCards aria-label="admin accounts" className="w-full min-w-[960px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">보기</TableHead>
                  <TableHead>계정</TableHead>
                  <TableHead>{application.label} 이용 상태 · 만료</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>가입일</TableHead>
                  <TableHead>최근 로그인</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listState.loading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className="flex items-center justify-center py-8 text-muted-foreground">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        계정 목록을 불러오는 중입니다...
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {!listState.loading && !listState.error && subscriptionFilterLoading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className="flex items-center justify-center py-8 text-muted-foreground">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        전체 계정의 이용·결제 상태를 확인하는 중입니다...
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {!listState.loading && !listState.error && subscriptionFilterUnavailable && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className="flex flex-col items-center gap-2 py-8 text-center text-amber-700">
                        <UserRound className="h-5 w-5" />
                        <span>계정 구분 필터를 적용할 수 없습니다. 위의 다시 시도를 눌러주세요.</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {!listState.loading
                  && !listState.error
                  && !subscriptionFilterLoading
                  && !subscriptionFilterUnavailable
                  && visibleCount === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
                        <UserRound className="h-5 w-5" />
                        <span>표시할 계정이 없습니다.</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {!listState.loading && !listState.error && paginatedAccounts.items.map((account) => {
                  const accountId = normalizeAdminAccountUserId(account);
                  const isExpanded = expandedAccountId === accountId;
                  const paymentSummary = scopedPaymentSummariesByUserId[accountId];
                  const rowKey = getAdminAccountUserKey(account) || getAdminAccountUserLabel(account);

                  return (
                    <Fragment key={rowKey}>
                      <TableRow
                        data-state={isExpanded ? 'selected' : undefined}
                        className="cursor-pointer"
                        onClick={() => toggleAccountRow(accountId)}
                      >
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`${getAdminAccountUserLabel(account)} ${isExpanded ? '상세 닫기' : '상세 보기'}`}
                            aria-expanded={isExpanded}
                            aria-controls={isExpanded ? `account-detail-${accountId}` : undefined}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleAccountRow(accountId);
                            }}
                          >
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                        <TableCell className="w-[250px] min-w-[190px]">
                          <div className="font-semibold text-foreground">{getAdminAccountUserLabel(account)}</div>
                          <div className="mt-1 break-all text-xs text-muted-foreground">{formatCompactText(account.email)}</div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            등록 앱: {account.registrationApplications.join(' · ') || '미식별'}
                          </div>
                        </TableCell>
                        <TableCell className="min-w-[300px] max-w-[480px]">
                          <AccountSummaryCell
                            summary={paymentSummary}
                            loading={summaryState.loading && !paymentSummary}
                            hasError={Boolean(summaryState.error)}
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant={account.enabled ? 'success' : 'secondary'}>
                            {formatAdminAccountStatus(account.enabled)}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                          {formatAdminAccountDateTime(account.createdDate)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                          {formatAdminAccountDateTime(account.lastLoginDate)}
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell id={`account-detail-${accountId}`} colSpan={6} className="bg-muted/40">
                            <ExpandedAccountDetails
                              account={account}
                              paymentSummary={paymentSummary}
                              paymentDebug={scopedPaymentDebug}
                              referralDebug={referralDebug}
                              loading={debugState.loading}
                              referralLoading={referralState.loading}
                              error={debugState.error}
                              referralError={referralState.error}
                              actionState={actionState}
                              onRequestRefund={handleRequestRefund}
                              onCancelSubscription={handleCancelSubscription}
                              showSubscriptionEvents={isSajugApplication}
                              onReloadReferralDebug={isSajugApplication ? handleReloadReferralDebug : undefined}
                              onResetReferralUsedHistory={isSajugApplication ? handleResetReferralUsedHistory : undefined}
                              onResetReferralReceivedHistory={isSajugApplication ? handleResetReferralReceivedHistory : undefined}
                              onResetFirstLoginTrial={isSajugApplication ? handleResetFirstLoginTrial : undefined}
                              onGrantManualBenefit={isSajugApplication ? handleGrantManualBenefit : undefined}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {!listState.loading && !listState.error && visibleCount > 0 && (
            <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                {paginatedAccounts.startIndex}-{paginatedAccounts.endIndex} / {visibleCount} · 페이지 {paginatedAccounts.currentPage}/{paginatedAccounts.pageCount}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={paginatedAccounts.currentPage <= 1}
                >
                  이전
                </Button>
                <div className="min-w-[72px] text-center text-sm text-muted-foreground">
                  {paginatedAccounts.currentPage} / {paginatedAccounts.pageCount}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((page) => Math.min(paginatedAccounts.pageCount, page + 1))}
                  disabled={paginatedAccounts.currentPage >= paginatedAccounts.pageCount}
                >
                  다음
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SignupTrendCard({
  title,
  previousLabel,
  trend,
}: {
  title: string;
  previousLabel: string;
  trend: AdminAccountSignupTrend;
}) {
  const TrendIcon = trend.deltaCount > 0 ? TrendingUp : trend.deltaCount < 0 ? TrendingDown : Minus;

  return (
    <Card>
      <CardContent className="py-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="mt-1 text-3xl font-bold tracking-tight tabular-nums">{formatSignupTrendCount(trend.currentCount)}</p>
          </div>
          <Badge
            variant={trend.deltaCount > 0 ? 'success' : trend.deltaCount < 0 ? 'destructive' : 'secondary'}
            className="gap-1 whitespace-nowrap"
          >
            <TrendIcon className="h-3.5 w-3.5" />
            {formatSignupTrendDelta(trend)}
          </Badge>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          {previousLabel} {formatSignupTrendCount(trend.previousCount)} 대비
        </p>
      </CardContent>
    </Card>
  );
}

function PaymentInsightCard({
  title,
  description,
  trend,
  loading,
}: {
  title: string;
  description: string;
  trend: AdminAccountPaymentInsightTrendSummary[keyof AdminAccountPaymentInsightTrendSummary];
  loading: boolean;
}) {
  const TrendIcon = trend.deltaCount > 0 ? TrendingUp : trend.deltaCount < 0 ? TrendingDown : Minus;

  return (
    <Card>
      <CardContent className="py-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          </div>
          <Badge
            variant={trend.deltaCount > 0 ? 'success' : trend.deltaCount < 0 ? 'destructive' : 'secondary'}
            className="gap-1 whitespace-nowrap"
          >
            <TrendIcon className="h-3.5 w-3.5" />
            {loading ? '집계 중' : formatSignupTrendDelta(trend)}
          </Badge>
        </div>
        <p className="mt-3 text-3xl font-bold tracking-tight tabular-nums">
          {loading ? '—' : formatSignupTrendCount(trend.currentCount)}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          직전 달 {loading ? '—' : formatSignupTrendCount(trend.previousCount)} 대비
        </p>
      </CardContent>
    </Card>
  );
}

function CurrentPaymentInsightCard({
  title,
  description,
  count,
  loading,
}: {
  title: string;
  description: string;
  count: number;
  loading: boolean;
}) {
  return (
    <Card>
      <CardContent className="py-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          </div>
          <Badge variant="success" className="whitespace-nowrap">
            현재
          </Badge>
        </div>
        <p className="mt-3 text-3xl font-bold tracking-tight tabular-nums">
          {loading ? '—' : formatSignupTrendCount(count)}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          무료체험·쿠폰·추천인·테스트 구매 제외
        </p>
      </CardContent>
    </Card>
  );
}

function formatSignupTrendCount(value: number): string {
  return `${value.toLocaleString('ko-KR')}명`;
}

function formatSignupTrendDelta(trend: AdminAccountSignupTrend): string {
  if (trend.deltaCount === 0) {
    return trend.deltaPercent === null ? '변동 없음' : `변동 없음 · ${formatSignupTrendPercent(trend.deltaPercent)}`;
  }

  const deltaCount = `${trend.deltaCount > 0 ? '+' : ''}${trend.deltaCount.toLocaleString('ko-KR')}명`;
  if (trend.deltaPercent === null) {
    return `${deltaCount} · 신규`;
  }
  return `${deltaCount} · ${formatSignupTrendPercent(trend.deltaPercent)}`;
}

function formatSignupTrendPercent(value: number): string {
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
}
